class CollectionFilter extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.activeCategoryUrl = null;
  }

  connectedCallback() {
    setTimeout(() => {
      this.initializeFilterButtons();
      this.setupEventListeners();
      this.initStickyChips();
    }, 100);
  }

  initStickyChips() {
    const stickyWrap = this.closest('.collection-category-chips-sticky');
    if (!stickyWrap) return;

    const mobileQuery = window.matchMedia('(max-width: 749px)');

    const getStickyHeaderBottom = () => {
      const sectionHeader = document.querySelector('.section-header');
      if (!sectionHeader) return 0;

      const stickyOffset = parseFloat(window.getComputedStyle(sectionHeader).top) || 0;
      return stickyOffset + sectionHeader.offsetHeight;
    };

    const updateStickyTop = () => {
      if (!mobileQuery.matches) {
        stickyWrap.classList.remove('is-stuck');
        stickyWrap.style.removeProperty('--collection-chips-sticky-top');
        return;
      }

      stickyWrap.style.setProperty('--collection-chips-sticky-top', `${getStickyHeaderBottom()}px`);
    };

    const getStickyTop = () => parseFloat(window.getComputedStyle(stickyWrap).top) || 0;

    const updateStuckState = () => {
      if (!mobileQuery.matches) {
        stickyWrap.classList.remove('is-stuck');
        return;
      }

      const stickyTop = getStickyTop();
      const isStuck = stickyWrap.getBoundingClientRect().top <= stickyTop + 1;
      stickyWrap.classList.toggle('is-stuck', isStuck);
    };

    const onScrollOrResize = () => {
      updateStickyTop();
      updateStuckState();
    };

    updateStickyTop();
    updateStuckState();
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    window.addEventListener('scroll', onScrollOrResize, { passive: true });

    mobileQuery.addEventListener('change', () => {
      updateStickyTop();
      updateStuckState();
    });
  }

  initializeFilterButtons() {
    this.filterButtons = this.querySelectorAll('[data-category-url]');
    this.productGridContainer = document.getElementById('ProductGridContainer');

    if (!this.productGridContainer || this.filterButtons.length === 0) {
      return;
    }

    const defaultButton = this.querySelector('[data-is-all]') || this.filterButtons[0];
    if (defaultButton && !this.querySelector('.filter-btn.active')) {
      defaultButton.classList.add('active');
    }

    const activeButton = this.querySelector('.filter-btn.active');
    if (activeButton) {
      const activeUrl = activeButton.getAttribute('data-category-url');
      this.activeCategoryUrl = activeButton.hasAttribute('data-is-all')
        ? null
        : this.normalizeFilterUrl(activeUrl);
    }
  }

  setupEventListeners() {
    this.filterButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();

        if (button.classList.contains('active')) {
          if (button.hasAttribute('data-is-all')) {
            return;
          }

          this.handleFilterRemove(button);
          return;
        }

        this.handleFilterClick(button);
      });
    });
  }

  handleFilterClick(button) {
    if (this.isLoading) return;

    const categoryUrl = button.getAttribute('data-category-url');
    if (!categoryUrl) return;

    if (button.hasAttribute('data-is-all')) {
      if (this.activeCategoryUrl === null) return;

      this.updateActiveButton(button);
      this.activeCategoryUrl = null;
      this.showPagination();
      this.showLoadingState();
      this.fetchFilteredProducts(categoryUrl);
      return;
    }

    const normalizedCategoryUrl = this.normalizeFilterUrl(categoryUrl);
    if (normalizedCategoryUrl === this.activeCategoryUrl) {
      return;
    }

    this.updateActiveButton(button);
    this.activeCategoryUrl = normalizedCategoryUrl;
    this.hidePagination();
    this.showLoadingState();
    this.fetchFilteredProducts(categoryUrl);
  }

  handleFilterRemove(button) {
    if (this.isLoading) return;

    const allButton = this.querySelector('[data-is-all]') || this.filterButtons[0];
    if (allButton) {
      this.updateActiveButton(allButton);
    } else {
      button.classList.remove('active');
    }

    this.activeCategoryUrl = null;
    this.scrollActiveFiltersToFirstButtonOnMobile();
    this.showPagination();
    this.showLoadingState();

    const defaultUrl = window.location.pathname;
    this.fetchFilteredProducts(defaultUrl);
  }

  isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  scrollActiveFiltersToFirstButtonOnMobile() {
    if (!this.isMobile()) return;

    const firstFilterButton = this.querySelector('.filter-btn');
    if (!firstFilterButton) return;

    const container = this;
    const containerStyle = window.getComputedStyle(container);
    const paddingLeft = parseFloat(containerStyle.paddingLeft) || 0;
    const targetScrollLeft = Math.max(firstFilterButton.offsetLeft - paddingLeft, 0);

    if (typeof container.scrollTo === 'function') {
      container.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
    } else {
      container.scrollLeft = targetScrollLeft;
    }
  }

  normalizeFilterUrl(url) {
    if (!url) return '';

    let normalized = url;
    try {
      normalized = new URL(url, window.location.origin).pathname;
    } catch (error) {
      normalized = url.split('?')[0];
    }

    return normalized.replace(/\/$/, '');
  }

  updateActiveButton(button) {
    this.filterButtons.forEach((btn) => {
      btn.classList.remove('active');
    });
    button.classList.add('active');
  }

  showLoadingState() {
    this.isLoading = true;

    if (!this.fullPageOverlay) {
      this.fullPageOverlay = document.createElement('div');
      this.fullPageOverlay.className = 'collection-filter-overlay';
      this.fullPageOverlay.style.cssText =
        'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;';
      this.fullPageOverlay.innerHTML =
        '<div class="loading-overlay__spinner" style="width:40px;height:40px;border:3px solid #ccc;border-top-color:#000;border-radius:50%;animation:spin .6s linear infinite;"></div>';

      if (!document.getElementById('filter-spinner-keyframes')) {
        const style = document.createElement('style');
        style.id = 'filter-spinner-keyframes';
        style.textContent = '@keyframes spin{to{transform:rotate(360deg)}}';
        document.head.appendChild(style);
      }
    }

    document.body.appendChild(this.fullPageOverlay);
  }

  hideLoadingState() {
    this.isLoading = false;
    if (this.fullPageOverlay && this.fullPageOverlay.parentNode) {
      this.fullPageOverlay.parentNode.removeChild(this.fullPageOverlay);
    }
  }

  buildFetchUrl(categoryUrl) {
    let fetchUrl = categoryUrl;

    if (fetchUrl === '/collections' || fetchUrl === '/collections/') {
      const currentCollection = document
        .querySelector('[data-collection-handle]')
        ?.getAttribute('data-collection-handle');
      fetchUrl = currentCollection ? `/collections/${currentCollection}` : '/collections/all-products';
    }

    const url = new URL(fetchUrl, window.location.origin);
    const currentParams = new URLSearchParams(window.location.search);

    currentParams.forEach((value, key) => {
      if (key !== 'page') {
        url.searchParams.set(key, value);
      }
    });

    return `${url.pathname}${url.search}`;
  }

  async fetchFilteredProducts(categoryUrl) {
    try {
      const fetchUrl = this.buildFetchUrl(categoryUrl);
      const response = await fetch(fetchUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      this.updateProductGrid(html);
      this.hideLoadingState();
    } catch (error) {
      console.error('Error fetching filtered products:', error);
      this.hideLoadingState();
      this.showErrorMessage('Failed to load products. Please try again.');
    }
  }

  getResetUrl() {
    return this.getAttribute('data-reset-url') || window.location.pathname;
  }

  getEmptyStateNode(sectionId) {
    const template = document.getElementById('collection-category-chips-empty-template');
    if (!template) return null;

    const emptyNode = template.content.firstElementChild.cloneNode(true);
    if (sectionId) {
      emptyNode.setAttribute('data-id', sectionId);
    }

    const clearLink = emptyNode.querySelector('.collection-category-chips-empty__clear');
    if (clearLink) {
      clearLink.href = this.getResetUrl();
    }

    return emptyNode;
  }

  isEmptyProductGrid(productGrid) {
    if (!productGrid) return false;

    if (productGrid.classList.contains('collection--empty')) {
      return true;
    }

    return productGrid.tagName === 'UL' && productGrid.querySelectorAll('.grid__item').length === 0;
  }

  updateProductGrid(html) {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newProductGrid = doc.querySelector('#product-grid');
      const oldProductGrid = this.productGridContainer.querySelector('#product-grid');

      if (!newProductGrid || !oldProductGrid) {
        this.showErrorMessage('Could not update products. Please refresh the page.');
        return;
      }

      const sectionId = oldProductGrid.getAttribute('data-id');

      if (this.isEmptyProductGrid(newProductGrid)) {
        const emptyNode = this.getEmptyStateNode(sectionId);
        if (emptyNode) {
          oldProductGrid.replaceWith(emptyNode);
        } else {
          const clonedGrid = newProductGrid.cloneNode(true);
          this.fixEmptyStateLink(clonedGrid);
          oldProductGrid.replaceWith(clonedGrid);
        }
      } else {
        oldProductGrid.replaceWith(newProductGrid.cloneNode(true));
        this.normalizePriceDisplay(this.productGridContainer.querySelector('#product-grid'));
      }

      this.reinitializeProductCards();
    } catch (error) {
      console.error('Error parsing product grid:', error);
      this.showErrorMessage('Failed to parse products. Please try again.');
    }
  }

  fixEmptyStateLink(emptyGrid) {
    const resetUrl = this.getResetUrl();
    emptyGrid.querySelectorAll('a').forEach((link) => {
      link.href = resetUrl;
      link.classList.add('collection-category-chips-empty__clear');
    });
  }

  normalizePriceDisplay(container) {
    if (!window.location.pathname.startsWith('/collections/')) {
      return;
    }

    const mrpSpans = container.querySelectorAll('.mrrp, .mrrp-ab');
    mrpSpans.forEach((span) => {
      const rupeeSpan = document.createElement('span');
      rupeeSpan.className = 'rupee_symbol';
      rupeeSpan.textContent = '₹';
      span.replaceWith(rupeeSpan);
    });
  }

  hidePagination() {
    const pagination = this.productGridContainer
      ? this.productGridContainer.querySelector('.pagination-wrapper')
      : document.querySelector('#ProductGridContainer .pagination-wrapper');
    if (pagination) {
      pagination.style.display = 'none';
    }
  }

  showPagination() {
    const pagination = this.productGridContainer
      ? this.productGridContainer.querySelector('.pagination-wrapper')
      : document.querySelector('#ProductGridContainer .pagination-wrapper');
    if (pagination) {
      pagination.style.display = '';
    }
  }

  reinitializeProductCards() {
    document.dispatchEvent(
      new CustomEvent('collectionFiltered', {
        detail: { grid: this.productGridContainer },
      })
    );

    if (window.themeAnimations && window.themeAnimations.init) {
      window.themeAnimations.init();
    }
  }

  showErrorMessage(message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'collection-filter-error';
    errorDiv.textContent = message;
    errorDiv.style.cssText =
      'padding:1rem;background-color:#ffeaea;color:#d32f2f;border-radius:4px;margin:1rem 0;text-align:center;';

    this.productGridContainer.insertBefore(errorDiv, this.productGridContainer.firstChild);

    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  }
}

if (!customElements.get('collection-filter')) {
  customElements.define('collection-filter', CollectionFilter);
}
